// TODO: from complexity this was left out of the refactor, it should be refactored
import React, { Component, createRef, RefObject } from 'react';
// import { SbEditableContent } from 'storyblok-react';
import debounce from 'debounce';
import { breakpoints } from '@rocheglobal/component-library-dot-com/src/style/base';
// To prevent issues with page calculation, we must cancel React >= 18 new automatic state batches
import { flushSync } from 'react-dom';
import { blokToComponent } from '../../helpers/blok-to-component';

interface Tracker {
  page: number;
  total: number;
}
interface TeaserWallState {
  activeFilter: string;
  teasers: any[];
  tracker: Record<string, Tracker>;
}

export class RocheTeaserWall extends Component<any, TeaserWallState> {
  private elementRef: RefObject<HTMLElement>;

  private loadMoreAmount = 9;

  constructor(props) {
    super(props);

    this.state = {
      activeFilter: undefined,
      teasers: this.props.blok.teasers,
      tracker: this.buildTracker(),
    };

    this.loadMore = this.loadMore.bind(this);
    this.handleResize = debounce(this.handleResize.bind(this), 300);
    this.elementRef = createRef();
  }

  public componentDidMount(): void {
    this.elementRef.current.addEventListener('rocheLoadMore', this.loadMore);
    window.addEventListener('resize', this.handleResize);

    // remove when teaser wall is moved to react world:
    // currently needed because ::slotted selector has less specificity to reset margin-top
    Array.from(this.elementRef.current.children).forEach((child: Element) => {
      (child as HTMLElement).style.marginTop = '0';
    });
  }

  public render(): JSX.Element {
    const { getComponent, slot } = this.props;
    const { teasers } = this.state;

    return (
      <roche-teaser-wall
        ref={this.elementRef}
        can-load-more={this.canLoadMore}
        is-fetching={this.isFetching}
        slot={slot}
      >
        {teasers.map((teaser, index) =>
          blokToComponent(
            {
              blok: teaser,
              getComponent,
              updateHoistedState: (rendered: number, total: number, id: string) => {
                if (!this.elementRef.current) {
                  return;
                }
                const { tracker: oldTracker } = this.state;
                const updatedTracker = {
                  ...oldTracker,
                  [id]: {
                    ...oldTracker[id],
                    // if no item was rendered, it should still count as a page increment
                    page: (oldTracker[id]?.page || 0) + (rendered || 1),
                    total,
                  },
                };

                flushSync(() => {
                  this.setState({ tracker: updatedTracker });
                });
              },
              keyForChildren: index,
              slot: 'teasers',
            },
            `${index}-teaser`,
          ),
        )}
        {/* Render the next page, so we can predict if load more can be displayed */}
        {this.nextTeasers.map((teaser, index) =>
          blokToComponent(
            {
              blok: teaser,
              getComponent,
              /*
               * We call render because this.canLoadMore needs to be updated
               * each time we render one of the "next page teasers"
               */
              updateHoistedState: () => this.render(),
              keyForChildren: index,
              slot: 'next-page-teasers',
            },
            `${index}-next-page-teaser`,
          ),
        )}
      </roche-teaser-wall>
    );
  }

  private get canLoadMore(): boolean {
    const { disable_load_more: disableLoadMore } = this.props.blok;

    if (disableLoadMore || this.nextTeasers.length <= 0) {
      return false;
    }

    const { activeFilter, tracker } = this.state;

    if (!activeFilter) {
      return Object.values(tracker).some((item) => item.page < item.total);
    }

    // next page teasers matching active filter > 0
    return this.elementRef.current.querySelectorAll(`*[slot="next-page-teasers"][tag="${activeFilter}"]`).length > 0;
  }

  private get isFetching(): boolean {
    const totalRendered = Object.values(this.state.tracker).reduce((accumulator, item) => accumulator + item.page, 0);
    return totalRendered < this.totalExpectedItems;
  }

  private get totalExpectedItems(): number {
    return this.state.teasers.reduce((accumulator, item) => {
      const expectedCappedAtTotalMax = Math.min(
        Number(item.teaser_number) || 1,
        this.state.tracker[`${item.tags}${item.group_tags}`]?.total || 1,
      );

      return accumulator + expectedCappedAtTotalMax;
    }, 0);
  }

  private get nextTeasers(): any[] {
    const { tracker, activeFilter } = this.state;
    const arrayLength = Math.ceil(this.loadMoreAmount / this.teaserAutoList.length);

    return this.teaserAutoList
      .filter((item) => !activeFilter || item.tags.some((tag: string) => tag === activeFilter))
      .map((item) =>
        Array.from({ length: arrayLength }, (_, index) => index + 1).map((count) => {
          const { page, total } = tracker[`${item.tags}${item.group_tags}`];
          const nextPage = page + count;
          return nextPage > total ? [] : { ...item, page: nextPage, teaser_number: 1 };
        }),
      )
      .flat(2);
  }

  private loadMore(): void {
    this.assignLoadMore(this.totalExpectedItems);
    flushSync(() => {
      this.setState({ teasers: [...this.state.teasers, ...this.nextTeasers] });
    });
  }

  private get teaserAutoList(): any[] {
    return this.props.blok.teasers.filter((item: any) =>
      ['roche-teaser-auto', 'roche-story-teaser-auto'].includes(item.component),
    );
  }

  private buildTracker(): Record<string, Tracker> {
    return this.teaserAutoList.reduce((accumulator, item): Tracker => {
      accumulator[`${item.tags}${item.group_tags}`] = {
        page: 0,
        total: 0,
      };
      return accumulator as Tracker;
    }, {});
  }

  handleResize(): void {
    this.assignLoadMore(this.totalExpectedItems);
  }

  assignLoadMore(expectedItems: number): void {
    const [loadMoreAmount, module] = this.isMediumScreen ? [8, 2] : [9, 3];
    this.loadMoreAmount = this.calculateLoadMore(expectedItems, module, loadMoreAmount);
  }

  private calculateLoadMore(expectedItems: number, module: number, loadMore: number): number {
    if (expectedItems % module === 2) {
      return loadMore + 1;
    }
    if (expectedItems % module === 1) {
      return loadMore - 1;
    }
    return loadMore;
  }

  private get clientWidth(): number {
    return typeof document !== 'undefined' ? document.body.clientWidth : null;
  }

  private get isMediumScreen(): boolean {
    return !!(this.clientWidth >= breakpoints.medium && this.clientWidth < breakpoints.large);
  }
}
